import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { 
  FileCode, 
  Download,
  CheckCircle,
  Calendar,
  ArrowRight,
  AlertCircle
} from "lucide-react";

export default function GeneraXML() {
  const [scadenze, setScadenze] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [generatedXml, setGeneratedXml] = useState(null);
  const [step, setStep] = useState(1);

  useEffect(() => {
    const fetchScadenze = async () => {
      try {
        const res = await axios.get(`${API}/scadenze?stato=pending`);
        setScadenze(res.data);
      } catch (e) {
        toast.error("Errore nel caricamento scadenze");
      } finally {
        setLoading(false);
      }
    };
    fetchScadenze();
  }, []);

  const handleSelectAll = () => {
    if (selectedIds.length === scadenze.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(scadenze.map(s => s.id));
    }
  };

  const handleSelect = (id) => {
    if (selectedIds.includes(id)) {
      setSelectedIds(selectedIds.filter(i => i !== id));
    } else {
      setSelectedIds([...selectedIds, id]);
    }
  };

  const selectedScadenze = scadenze.filter(s => selectedIds.includes(s.id));
  const selectedTotal = selectedScadenze.reduce((sum, s) => sum + s.importo, 0);
  
  // Group by data_incasso for display
  const groupedByDate = selectedScadenze.reduce((acc, s) => {
    if (!acc[s.data_incasso]) {
      acc[s.data_incasso] = { count: 0, amount: 0 };
    }
    acc[s.data_incasso].count += 1;
    acc[s.data_incasso].amount += s.importo;
    return acc;
  }, {});

  const handleGenerate = async () => {
    if (selectedIds.length === 0) {
      toast.error("Seleziona almeno una scadenza");
      return;
    }

    setGenerating(true);
    try {
      const params = new URLSearchParams();
      selectedIds.forEach(id => params.append('scadenze_ids', id));
      
      const res = await axios.post(`${API}/xml/generate?${params.toString()}`);
      setGeneratedXml(res.data);
      setStep(2);
      toast.success("File XML generato con successo!");
    } catch (e) {
      toast.error("Errore nella generazione XML");
      console.error(e);
    } finally {
      setGenerating(false);
    }
  };

  const handleDownload = () => {
    if (!generatedXml) return;
    
    const blob = new Blob([generatedXml.xml_content], { type: 'application/xml' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = generatedXml.filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success("Download avviato");
  };

  const resetWizard = () => {
    setStep(1);
    setSelectedIds([]);
    setGeneratedXml(null);
    // Refresh scadenze
    setLoading(true);
    axios.get(`${API}/scadenze?stato=pending`)
      .then(res => setScadenze(res.data))
      .finally(() => setLoading(false));
  };

  return (
    <div className="animate-fade-in space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-900" data-testid="genera-xml-title">
          Genera File XML SEPA B2B
        </h1>
        <p className="text-slate-500 mt-1">
          Seleziona le scadenze e genera il file per Unicredit
        </p>
      </div>

      {/* Wizard Steps */}
      <div className="flex items-center gap-4 mb-8">
        {[1, 2].map((s) => (
          <div key={s} className="flex items-center">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors ${
              step >= s 
                ? 'bg-slate-900 text-white' 
                : 'bg-slate-200 text-slate-500'
            }`}>
              {step > s ? <CheckCircle className="w-4 h-4" /> : s}
            </div>
            <span className={`ml-2 text-sm font-medium ${step >= s ? 'text-slate-900' : 'text-slate-400'}`}>
              {s === 1 && "Seleziona e Genera"}
              {s === 2 && "Download"}
            </span>
            {s < 2 && <ArrowRight className="w-4 h-4 text-slate-300 mx-4" />}
          </div>
        ))}
      </div>

      {/* Step 1: Select Scadenze and Generate */}
      {step === 1 && (
        <Card data-testid="step-1">
          <CardHeader>
            <CardTitle className="text-lg">Seleziona le scadenze da includere</CardTitle>
            <CardDescription>
              {scadenze.length} scadenze in attesa di elaborazione. 
              Le scadenze verranno raggruppate automaticamente per data di incasso.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="space-y-3">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="h-16 bg-slate-100 rounded animate-pulse" />
                ))}
              </div>
            ) : scadenze.length === 0 ? (
              <div className="text-center py-12">
                <AlertCircle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
                <p className="text-slate-700 font-medium">Nessuna scadenza in attesa</p>
                <p className="text-sm text-slate-500 mt-1">
                  Importa o aggiungi scadenze dalla sezione Scadenze, oppure resetta lo stato delle scadenze elaborate
                </p>
              </div>
            ) : (
              <>
                <div className="mb-4 flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <Checkbox 
                      checked={selectedIds.length === scadenze.length && scadenze.length > 0}
                      onCheckedChange={handleSelectAll}
                      data-testid="select-all-checkbox"
                    />
                    <span className="text-sm font-medium">
                      Seleziona tutto ({scadenze.length})
                    </span>
                  </div>
                  <div className="text-sm">
                    <span className="text-slate-500">Selezionate: </span>
                    <span className="font-semibold">{selectedIds.length}</span>
                    <span className="mx-2 text-slate-300">|</span>
                    <span className="text-slate-500">Totale: </span>
                    <span className="font-semibold">
                      € {selectedTotal.toLocaleString('it-IT', { minimumFractionDigits: 2 })}
                    </span>
                  </div>
                </div>

                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {scadenze.map((scadenza) => (
                    <div 
                      key={scadenza.id}
                      className={`flex items-center gap-4 p-4 border rounded-lg transition-colors cursor-pointer ${
                        selectedIds.includes(scadenza.id) 
                          ? 'border-slate-900 bg-slate-50' 
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                      onClick={() => handleSelect(scadenza.id)}
                      data-testid={`scadenza-item-${scadenza.id}`}
                    >
                      <Checkbox 
                        checked={selectedIds.includes(scadenza.id)}
                        onCheckedChange={() => handleSelect(scadenza.id)}
                      />
                      <div className="flex-1">
                        <p className="font-medium text-slate-900">{scadenza.cliente_nominativo}</p>
                        <p className="text-xs text-slate-500 mt-1">
                          {scadenza.numero_fattura && `Fattura ${scadenza.numero_fattura} • `}
                          Scadenza: {scadenza.data_scadenza}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold tabular-nums">
                          € {scadenza.importo.toLocaleString('it-IT', { minimumFractionDigits: 2 })}
                        </p>
                        <Badge variant="outline" className="mt-1 text-xs">
                          <Calendar className="w-3 h-3 mr-1" />
                          Incasso: {scadenza.data_incasso}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Riepilogo per data */}
                {selectedIds.length > 0 && (
                  <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <h4 className="font-medium text-sm text-blue-900 mb-3">
                      Riepilogo distinta XML - Raggruppamento per data incasso:
                    </h4>
                    <div className="space-y-2">
                      {Object.entries(groupedByDate).sort().map(([date, data]) => (
                        <div key={date} className="flex items-center justify-between text-sm">
                          <span className="text-blue-800">
                            <Calendar className="w-4 h-4 inline mr-2" />
                            {date}
                          </span>
                          <span className="font-medium text-blue-900">
                            {data.count} transazioni - € {data.amount.toLocaleString('it-IT', { minimumFractionDigits: 2 })}
                          </span>
                        </div>
                      ))}
                    </div>
                    <div className="mt-3 pt-3 border-t border-blue-200 flex items-center justify-between font-semibold text-blue-900">
                      <span>TOTALE DISTINTA</span>
                      <span>
                        {selectedIds.length} transazioni - € {selectedTotal.toLocaleString('it-IT', { minimumFractionDigits: 2 })}
                      </span>
                    </div>
                  </div>
                )}

                <div className="mt-6 flex justify-end">
                  <Button 
                    onClick={handleGenerate} 
                    disabled={selectedIds.length === 0 || generating}
                    data-testid="generate-xml-btn"
                    className="gap-2"
                  >
                    {generating ? (
                      <>Generazione in corso...</>
                    ) : (
                      <>
                        <FileCode className="w-4 h-4" />
                        Genera XML ({selectedIds.length} scadenze)
                      </>
                    )}
                  </Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* Step 2: Download */}
      {step === 2 && generatedXml && (
        <Card data-testid="step-2">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-emerald-600" />
              File XML generato con successo!
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="p-4 bg-slate-50 rounded-lg">
                  <p className="text-xs text-slate-500 uppercase tracking-wider">Nome File</p>
                  <p className="font-mono text-sm mt-1">{generatedXml.filename}</p>
                </div>
                <div className="p-4 bg-slate-50 rounded-lg">
                  <p className="text-xs text-slate-500 uppercase tracking-wider">Transazioni</p>
                  <p className="font-semibold text-lg mt-1">{generatedXml.num_transactions}</p>
                </div>
                <div className="p-4 bg-slate-50 rounded-lg">
                  <p className="text-xs text-slate-500 uppercase tracking-wider">Importo Totale</p>
                  <p className="font-semibold text-lg mt-1">
                    € {generatedXml.total_amount.toLocaleString('it-IT', { minimumFractionDigits: 2 })}
                  </p>
                </div>
              </div>

              <div className="space-y-2">
                <p className="text-sm font-medium text-slate-700">Anteprima XML</p>
                <div className="xml-preview max-h-64 overflow-auto">
                  <pre>{generatedXml.xml_content}</pre>
                </div>
              </div>

              <div className="flex justify-between">
                <Button variant="outline" onClick={resetWizard} data-testid="new-generation-btn">
                  Nuova Generazione
                </Button>
                <Button onClick={handleDownload} data-testid="download-xml-btn" className="gap-2">
                  <Download className="w-4 h-4" />
                  Scarica XML
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
